import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  FormControlLabel,
  Checkbox,
  Alert,
  Typography,
  useTheme
} from '@mui/material';
import PetsIcon from '@mui/icons-material/Pets';
import ImageIcon from '@mui/icons-material/Image';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const AddGatPopup = ({ open, onClose, onSubmit, initialData = null }) => {
  const createEmptyCat = () => ({
    name: "",
    breed: "",
    age: "",
    gender: "",
    color: "",
    size: "",
    description: "",
    health_status: "",
    vaccination_status: 0,
    neutered: 0,
    arrival_date: "",
    images: [],
  });

  const cleanupObjectUrls = (images = []) => {
    images.forEach((img) => {
      if (img?.preview && typeof img.preview === 'string' && img.preview.startsWith('blob:')) {
        URL.revokeObjectURL(img.preview);
      }
    });
  };

  const theme = useTheme();
  const [newCat, setNewCat] = useState(initialData || createEmptyCat());
  const [catImageInputs, setCatImageInputs] = useState(1);
  const [errorMessage, setErrorMessage] = useState("");

  const handleCatFileChange = (event, index) => {
    const files = Array.from(event.target.files);
    const updatedImages = [...newCat.images];
    
    files.forEach((file, i) => {
      const newIndex = index + i;
      if (newIndex < 4) {
        const existing = updatedImages[newIndex];
        if (existing?.preview && typeof existing.preview === 'string' && existing.preview.startsWith('blob:')) {
          URL.revokeObjectURL(existing.preview);
        }
        updatedImages[newIndex] = {
          file,
          preview: URL.createObjectURL(file),
          isPrimary: newIndex === 0
        };
      }
    });
  
    setNewCat({ ...newCat, images: updatedImages });
    setCatImageInputs(Math.min(updatedImages.length + 1, 4));
  };

  useEffect(() => {
    if (!open) {
      cleanupObjectUrls(newCat.images);
      setNewCat(createEmptyCat());
      setCatImageInputs(1);
      setErrorMessage("");
      return;
    }

    if (initialData) {
      const formattedImages = (initialData.images || [])
        .map((img, index) => {
          if (!img) return null;
          if (typeof img === 'string') {
            return {
              file: null,
              preview: img,
              isPrimary: index === 0,
            };
          }
          return {
            ...img,
            preview: typeof img.preview === 'string' ? img.preview : img.preview ?? '',
          };
        })
        .filter(Boolean);

      const formatted = {
        ...initialData,
        images: formattedImages,
      };
      setNewCat(formatted);
      setCatImageInputs(Math.min((formatted.images?.length || 0) + 1, 4));
    } else {
      setNewCat(createEmptyCat());
      setCatImageInputs(1);
    }
  }, [open, initialData]);

  const handleSetCatPrimary = (index) => {
    const updatedImages = newCat.images.map((img, i) => ({
      ...img,
      isPrimary: i === index
    }));
    setNewCat({ ...newCat, images: updatedImages });
  };

  const resetAndClose = () => {
    cleanupObjectUrls(newCat.images);
    setNewCat(createEmptyCat());
    setCatImageInputs(1);
    setErrorMessage("");
    onClose();
  };

  const handleSubmit = () => {
    const fieldsToCheck = [
      newCat.name,
      newCat.breed,
      newCat.age,
      newCat.gender,
      newCat.color,
      newCat.size,
      newCat.description,
      newCat.health_status,
      newCat.arrival_date
    ];

    const allEmpty = fieldsToCheck.every((value) => !value);
    const hasImages = newCat.images.length > 0;

    if (allEmpty && !hasImages) {
      setErrorMessage("Preencha pelo menos um campo ou adicione uma imagem antes de guardar.");
      return;
    }

    setErrorMessage("");
    onSubmit(newCat);
  };

  return (
    <Dialog
      open={open}
      onClose={resetAndClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: `2px solid ${theme.palette.primary.light}`,
          boxShadow: theme.shadows[10]
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PetsIcon sx={{ color: theme.palette.primary.main }} />
        Afegir un nou gat
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Box
          sx={{
            borderRadius: 2,
            border: `1px dashed ${theme.palette.primary.light}`,
            backgroundColor: theme.palette.action.hover,
            px: 3,
            py: 3
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <InfoOutlinedIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Informació del gat
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nom"
                fullWidth
                value={newCat.name}
                onChange={(e) => setNewCat({ ...newCat, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Raça"
                fullWidth
                value={newCat.breed}
                onChange={(e) => setNewCat({ ...newCat, breed: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Edat"
                fullWidth
                value={newCat.age}
                onChange={(e) => setNewCat({ ...newCat, age: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Gènere</InputLabel>
                <Select
                  value={newCat.gender}
                  label="Gènere"
                  onChange={(e) => setNewCat({ ...newCat, gender: e.target.value })}
                >
                  <MenuItem value="Macho">Macho</MenuItem>
                  <MenuItem value="Hembra">Hembra</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Mida</InputLabel>
                <Select
                  value={newCat.size}
                  label="Mida"
                  onChange={(e) => setNewCat({ ...newCat, size: e.target.value })}
                >
                  <MenuItem value="Pequeño">Pequeño</MenuItem>
                  <MenuItem value="Médio">Médio</MenuItem>
                  <MenuItem value="Grande">Grande</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Color"
                fullWidth
                value={newCat.color}
                onChange={(e) => setNewCat({ ...newCat, color: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Descripció"
                fullWidth
                multiline
                minRows={3}
                value={newCat.description}
                onChange={(e) => setNewCat({ ...newCat, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Estat de salut"
                fullWidth
                value={newCat.health_status}
                onChange={(e) => setNewCat({ ...newCat, health_status: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newCat.vaccination_status === 1}
                    onChange={(e) =>
                      setNewCat({ ...newCat, vaccination_status: e.target.checked ? 1 : 0 })
                    }
                  />
                }
                label="Vacunat"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newCat.neutered === 1}
                    onChange={(e) =>
                      setNewCat({ ...newCat, neutered: e.target.checked ? 1 : 0 })
                    }
                  />
                }
                label="Esterilitzat"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Data d'arribada"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={newCat.arrival_date}
                onChange={(e) => setNewCat({ ...newCat, arrival_date: e.target.value })}
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <ImageIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Imatges
            </Typography>
          </Box>
          {[...Array(catImageInputs)].map((_, index) => {
            const image = newCat.images[index];
            const previewSrc = image
              ? (typeof image === 'string' ? image : image.preview || '')
              : '';
            return (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 1.5,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  mb: 1
                }}
              >
                <Button variant="outlined" component="label" size="small">
                  Seleccionar
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => handleCatFileChange(e, index)}
                  />
                </Button>
                {image ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {previewSrc ? (
                      <Box
                        component="img"
                        src={previewSrc}
                        alt={`preview-${index}`}
                        sx={{ width: 72, height: 72, borderRadius: 1, objectFit: 'cover' }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 72,
                          height: 72,
                          borderRadius: 1,
                          border: `1px dashed ${theme.palette.divider}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <ImageIcon fontSize="small" color="disabled" />
                      </Box>
                    )}
                    <Button
                      variant={image.isPrimary ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => handleSetCatPrimary(index)}
                    >
                      Principal
                    </Button>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Cap imatge seleccionada
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>

        {errorMessage && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            {errorMessage}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button variant="outlined" onClick={resetAndClose}>
          Cancel·lar
        </Button>
        <Button variant="contained" onClick={handleSubmit}>
          {initialData ? "Guardar" : "Afegir"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
export default AddGatPopup;